import { Slider } from '@mui/material';
import { generateRandomData } from '@util/num';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { ChangeEvent, FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { TextSection } from '../TextSection';
import { FieldType } from '../types';
import { useTranslations } from 'next-intl';

interface PriceSliderProps {
  minPrice?: number | null;
  maxPrice?: number | null;
  handleSliderChange: (event: Event, newValue: number | number[]) => void;
  handleTextFieldChange: (e: ChangeEvent<HTMLInputElement>, field: FieldType, min?: 'min') => void;
  maxAllowedPrice: number;
  priceInterval?: number;
}

// Used for generating histogram / bar chart
const numberOfBars = 40;
const chartMin = 0;
const chartMax = 100;
const prices = generateRandomData(numberOfBars, chartMin, chartMax);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const PriceSlider: FC<PriceSliderProps> = ({
  minPrice,
  maxPrice,
  handleSliderChange,
  handleTextFieldChange,
  maxAllowedPrice,
  priceInterval,
}) => {
  const t = useTranslations();
  // Histogram / Bar Chart options
  // Hides scales, legend, tooltips
  const options = {
    responsive: true,
    tooltips: {
      enabled: false,
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
    },
    legend: {
      display: false,
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        min: 0,
      },
    },
  };

  // Sets data + data settings
  const data = {
    labels: prices.map((p, i) => i),
    datasets: [
      {
        label: t('Prices'),
        data: prices,
        // Background color changes if selected
        // If in range, make bars dark blue.
        // If not in range, make bars light blue.
        backgroundColor: prices.map((p, i) => {
          const min = minPrice ?? 0;
          const max = maxPrice ?? 10000000;
          const estValue =
            ((maxAllowedPrice <= max ? max : maxAllowedPrice) / numberOfBars) * (i + 1);
          const isInRange = estValue <= max && estValue >= min;
          return isInRange ? '#4B6B90' : '#E2EDF4';
        }),
      },
    ],
  };
  return (
    <>
      <Bar options={options} data={data} />
      <Slider
        getAriaLabel={() => 'Minimum distance'}
        value={[minPrice ?? 0, maxPrice ?? maxAllowedPrice]}
        onChange={handleSliderChange}
        disableSwap
        step={priceInterval}
        min={0}
        max={maxPrice && maxAllowedPrice <= maxPrice ? maxPrice : maxAllowedPrice}
        valueLabelDisplay="off"
        data-testid="price-slider"
        sx={{
          color: 'transparent',
          padding: '0',
          transform: 'translateY(-12px)',
          '& .MuiSlider-thumb': {
            color: '#4B6B90',
            border: '3px solid #fff',
            width: '23px',
            height: '23px',
          },
        }}
      />
      <TextSection
        minVal={minPrice}
        maxVal={maxPrice}
        handleTextFieldChange={handleTextFieldChange}
        field={'price'}
        isPrice={true}
      />
    </>
  );
};

export default PriceSlider;
